import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function Field({ f, value, onChange }) {
  const base = "mt-1 w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm outline-none focus:border-primary transition-colors";
  if (f.type === "select") {
    return (
      <div>
        <label className="text-xs font-medium text-muted-foreground">{f.label}{f.required && <span className="text-destructive"> *</span>}</label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`field-${f.key}`} className="mt-1 bg-secondary border-input"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 mt-6 cursor-pointer text-sm">
        <input type="checkbox" checked={!!value} data-testid={`field-${f.key}`} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-blue-500" />
        {f.label}
      </label>
    );
  }
  if (f.type === "textarea") {
    return (
      <div className="col-span-2">
        <label className="text-xs font-medium text-muted-foreground">{f.label}</label>
        <textarea value={value || ""} data-testid={`field-${f.key}`} onChange={(e) => onChange(e.target.value)} rows={2} className={base + " resize-none"} />
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{f.label}{f.required && <span className="text-destructive"> *</span>}</label>
      <input
        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
        value={value ?? ""}
        data-testid={`field-${f.key}`}
        onChange={(e) => onChange(f.type === "number" ? e.target.value : e.target.value)}
        className={base}
      />
    </div>
  );
}

export default function HoldingForm({ open, onOpenChange, product, editing, onSubmit }) {
  const hasSub = !!product?.subtypes;
  const [sub, setSub] = useState(hasSub ? product.subtypes[0].key : null);
  const [data, setData] = useState({});

  useEffect(() => {
    if (open) {
      if (editing) {
        setSub(editing.sub_type || (hasSub ? product.subtypes[0].key : null));
        setData(editing.data || {});
      } else {
        setSub(hasSub ? product.subtypes[0].key : null);
        setData({});
      }
    }
  }, [open, editing]);

  if (!product) return null;
  const fields = hasSub ? product.subtypes.find((s) => s.key === sub)?.fields || [] : product.fields;

  const submit = () => {
    for (const f of fields) {
      if (f.required && (data[f.key] === undefined || data[f.key] === "" || data[f.key] === null)) {
        toast.error(`${f.label} is required`);
        return;
      }
      if (f.type === "number" && data[f.key] !== undefined && data[f.key] !== "" && Number(data[f.key]) < 0) {
        toast.error(`${f.label} cannot be negative`);
        return;
      }
    }
    const clean = {};
    fields.forEach((f) => {
      let v = data[f.key];
      if (f.type === "number" && v !== undefined && v !== "") v = Number(v);
      if (v !== undefined) clean[f.key] = v;
    });
    onSubmit({ product_key: product.key, sub_type: sub, data: clean });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="card-surface max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "Add"} {product.label}</DialogTitle>
        </DialogHeader>

        {hasSub && (
          <div className="flex gap-2 mb-1">
            {product.subtypes.map((s) => (
              <button
                key={s.key}
                onClick={() => setSub(s.key)}
                data-testid={`subtype-${s.key}`}
                className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold border row-hover ${
                  sub === s.key ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {fields.map((f) => (
            <Field key={f.key} f={f} value={data[f.key]} onChange={(v) => setData((d) => ({ ...d, [f.key]: v }))} />
          ))}
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium row-hover hover:border-primary/40">Cancel</button>
          <button onClick={submit} data-testid="holding-save-button" className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-blue-500 transition-colors">Save Holding</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
